import { Card, Group, Stack, Text, ThemeIcon, Badge, Collapse, UnstyledButton } from '@mantine/core';
import { Calendar, ChevronDown, ChevronRight, Clock } from 'react-feather';
import { useState, type ReactNode } from 'react';
import type {
  SessionContentBlock,
  SessionContentParsedOutline,
} from '../../../../services/api/sessionContent';
import type {
  AiPromptSegmentSelections,
  AiPromptSelectionsDoc,
  CoachPrepGroupContext,
  CoachSessionLeaderNote,
  CoachLeaderNotesScope,
} from '../../../../services/api/coachSessionPrep';
import { LeaderNotesPanel } from './LeaderNotesPanel';
import { AIPromptSuggestionsSection } from './AIPromptSuggestionsSection';
import { AISuggestionsBanner } from './AISuggestionsBanner';

/**
 * Shared session agenda accordion.
 *
 * Originally shipped in MED-784 (E1.3) as the CM-only view; MED-793 extends
 * it with a `renderConfig` prop that flips on coach-mode features (Leader
 * Notes per step + group tabs, Mark As Reviewed, View Prep Sheet, Preview).
 *
 * Default mode is `cm` — pre-existing call sites in the CM editor continue
 * to work unchanged.
 *
 * Per Michelle's PM decision in MED-784 #48959, STEP CONTENT text is
 * populated from `block.text` extracted by the pdf-conversion microservice.
 */

// ---------------------------------------------------------------------------
// Render config
// ---------------------------------------------------------------------------

export interface SessionAgendaCoachContext {
  groups: CoachPrepGroupContext[];
  leaderNotes: CoachSessionLeaderNote[];
  /** Called when a leader note is saved (HTML body). Empty string deletes. */
  onSaveLeaderNote: (args: {
    blockKey: string;
    scope: CoachLeaderNotesScope;
    groupId: string | null;
    contentHtml: string;
  }) => Promise<void>;
  /** Optional header slot rendered at the top-right of the card header (Mark As Reviewed / Preview / View Prep Sheet). */
  headerActionsSlot?: ReactNode;
  /** When set, sections start collapsed (per AC4: "expanded by default until marked as reviewed"). */
  startCollapsed?: boolean;
  /** MED-876 — sessionId for the AI banner's View Prep Sheet action + the per-step AI fetch. */
  sessionId: string;
  /** MED-876 IV1 — when false, AI banner + per-step AI Suggestions sections are absent entirely. */
  aiFeaturesEnabled: boolean;
  /** MED-876 — current In Use selections doc; drives banner count + per-step badges. */
  aiPromptSelections: AiPromptSelectionsDoc;
  /** MED-876 — persist a single segment's updated selections; parent handles the doc merge + PATCH. */
  onSaveAiSelections: (segmentKey: string, next: AiPromptSegmentSelections) => Promise<void>;
}

export type SessionAgendaRenderConfig =
  | { mode: 'cm' }
  | { mode: 'coach'; coachContext: SessionAgendaCoachContext };

export interface SessionAgendaAccordionProps {
  outline: SessionContentParsedOutline;
  /** Optional render configuration. Defaults to CM mode (no coach features). */
  renderConfig?: SessionAgendaRenderConfig;
}

// ---------------------------------------------------------------------------
// Top-level accordion
// ---------------------------------------------------------------------------

export function SessionAgendaAccordion({
  outline,
  renderConfig = { mode: 'cm' },
}: SessionAgendaAccordionProps) {
  const totalDuration = outline.reduce((sum, section) => {
    return sum + section.children.reduce((s, b) => s + (b.duration_minutes ?? 0), 0);
  }, 0);
  const totalBlocks = outline.reduce((s, sec) => s + sec.children.length, 0);
  const stepsWithNotes =
    renderConfig.mode === 'coach'
      ? new Set(renderConfig.coachContext.leaderNotes.map((n) => n.block_key)).size
      : 0;

  const headerMetaText =
    renderConfig.mode === 'coach'
      ? `Total: ${totalDuration} minutes · ${stepsWithNotes} of ${totalBlocks} steps have leader notes`
      : `Total: ${totalDuration} minutes · ${totalBlocks} steps`;

  return (
    <Card withBorder padding={0} radius="md">
      {/* Card header — mirrors Screenshot 1's Session Agenda strip */}
      <Group justify="space-between" align="center" px="lg" py="md" wrap="nowrap">
        <Group align="center" gap="sm">
          <ThemeIcon variant="light" color="gray" size="md" radius="sm">
            <Calendar size={16} />
          </ThemeIcon>
          <Text fw={600}>Session Agenda</Text>
          <Badge variant="light" color="gray" radius="sm" tt="uppercase" fz={10}>
            All Groups
          </Badge>
          <Text size="sm" c="dimmed">
            {headerMetaText}
          </Text>
        </Group>
        {renderConfig.mode === 'coach' && renderConfig.coachContext.headerActionsSlot && (
          <Group gap="xs" wrap="nowrap">
            {renderConfig.coachContext.headerActionsSlot}
          </Group>
        )}
      </Group>

      {/* MED-876 AC1 — AI Suggestions banner. Hidden when count===0 (handled inside component). */}
      {renderConfig.mode === 'coach' && renderConfig.coachContext.aiFeaturesEnabled && (
        <div style={{ padding: '0 var(--mantine-spacing-lg) var(--mantine-spacing-sm)' }}>
          <AISuggestionsBanner
            sessionId={renderConfig.coachContext.sessionId}
            selections={renderConfig.coachContext.aiPromptSelections}
          />
        </div>
      )}

      {/* Section + Block rows */}
      <Stack gap={0}>
        {outline.map((section, sectionIdx) => (
          <SectionRow
            key={`${section.title}-${sectionIdx}`}
            section={section}
            sectionIdx={sectionIdx}
            isLast={sectionIdx === outline.length - 1}
            renderConfig={renderConfig}
          />
        ))}
      </Stack>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Section row
// ---------------------------------------------------------------------------

interface SectionRowProps {
  section: SessionContentParsedOutline[number];
  sectionIdx: number;
  isLast: boolean;
  renderConfig: SessionAgendaRenderConfig;
}

function SectionRow({ section, sectionIdx, isLast: _isLast, renderConfig }: SectionRowProps) {
  // AC4 — coach mode starts expanded UNTIL marked-as-reviewed flips startCollapsed.
  const initialOpen =
    renderConfig.mode === 'coach' && renderConfig.coachContext.startCollapsed ? false : true;
  const [open, setOpen] = useState(initialOpen);

  const sectionDuration = section.children.reduce((s, b) => s + (b.duration_minutes ?? 0), 0);

  return (
    <div
      style={{
        borderTop: '1px solid var(--mantine-color-gray-2)',
      }}
    >
      <UnstyledButton
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-md) var(--mantine-spacing-lg)',
        }}
      >
        <Group justify="space-between" align="center">
          <Group align="center" gap="md">
            <SectionNumber n={sectionIdx + 1} active={open} />
            <Text fw={600}>{section.title}</Text>
          </Group>
          <Group gap="sm" align="center">
            {sectionDuration > 0 && (
              <Group gap={4} align="center">
                <Clock size={14} />
                <Text size="sm" c="dimmed">
                  {sectionDuration} min
                </Text>
              </Group>
            )}
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={open}>
        <Stack gap={0} pb="sm">
          {section.children.length === 0 && (
            <Text size="sm" c="dimmed" px="lg" py="sm">
              No steps detected for this session.
            </Text>
          )}
          {section.children.map((block, blockIdx) => (
            <BlockRow
              key={`${block.title}-${blockIdx}`}
              block={block}
              blockIdx={blockIdx}
              sectionIdx={sectionIdx}
              renderConfig={renderConfig}
            />
          ))}
        </Stack>
      </Collapse>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Block row
// ---------------------------------------------------------------------------

interface BlockRowProps {
  block: SessionContentBlock;
  blockIdx: number;
  sectionIdx: number;
  renderConfig: SessionAgendaRenderConfig;
}

function BlockRow({ block, blockIdx, sectionIdx, renderConfig }: BlockRowProps) {
  const [open, setOpen] = useState(true);
  const blockKey = `${sectionIdx}:${blockIdx}`;

  return (
    <div style={{ paddingLeft: 'var(--mantine-spacing-xl)' }}>
      <UnstyledButton
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'block',
          width: '100%',
          padding: 'var(--mantine-spacing-sm) var(--mantine-spacing-lg)',
        }}
      >
        <Group justify="space-between" align="center">
          <Group align="center" gap="md">
            <BlockNumber sectionIdx={sectionIdx} blockIdx={blockIdx} active={open} />
            <Text fw={500} size="sm">
              {block.title}
            </Text>
          </Group>
          <Group gap="sm" align="center">
            {block.duration_minutes !== null && (
              <Group gap={4} align="center">
                <Clock size={12} />
                <Text size="xs" c="dimmed">
                  {block.duration_minutes} min
                </Text>
              </Group>
            )}
            {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </Group>
        </Group>
      </UnstyledButton>

      <Collapse in={open}>
        <div
          style={{
            padding:
              '0 var(--mantine-spacing-lg) var(--mantine-spacing-md) calc(var(--mantine-spacing-lg) + 36px)',
          }}
        >
          <StepContentBlock block={block} />
          {renderConfig.mode === 'coach' && (
            <LeaderNotesPanel
              blockKey={blockKey}
              groups={renderConfig.coachContext.groups}
              notes={renderConfig.coachContext.leaderNotes.filter(
                (n) => n.block_key === blockKey,
              )}
              onSave={renderConfig.coachContext.onSaveLeaderNote}
            />
          )}
          {/* MED-876 AC2 — Per-step AI Suggestions section. IV1: hidden when modality has AI disabled. */}
          {renderConfig.mode === 'coach' && renderConfig.coachContext.aiFeaturesEnabled && (
            <AIPromptSuggestionsSection
              sessionId={renderConfig.coachContext.sessionId}
              groupId={renderConfig.coachContext.groups[0]?.group_id}
              segmentIndex={blockIdx}
              segmentKey={blockKey}
              currentSelections={renderConfig.coachContext.aiPromptSelections[blockKey]}
              onSelectionsChange={(next) =>
                renderConfig.coachContext.onSaveAiSelections(blockKey, next)
              }
            />
          )}
        </div>
      </Collapse>
    </div>
  );
}

function StepContentBlock({ block }: { block: SessionContentBlock }) {
  if (!block.text || block.text.trim().length === 0) {
    return (
      <Text size="sm" c="dimmed" fs="italic">
        No content extracted for this step.
      </Text>
    );
  }
  return (
    <Card
      withBorder={false}
      radius="sm"
      padding="sm"
      style={{ background: 'var(--mantine-color-gray-0)' }}
    >
      <Text size="xs" c="dimmed" tt="uppercase" fw={600} fz={10} mb={4}>
        Step Content
      </Text>
      <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
        {block.text}
      </Text>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Numbered circles (unchanged from MED-784)
// ---------------------------------------------------------------------------

function SectionNumber({ n, active }: { n: number; active: boolean }) {
  return (
    <div
      style={{
        width: 32,
        height: 32,
        borderRadius: '50%',
        background: active ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-2)',
        color: active ? 'white' : 'var(--mantine-color-gray-7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 600,
        fontSize: 14,
        flexShrink: 0,
      }}
    >
      {n}
    </div>
  );
}

function BlockNumber({
  sectionIdx,
  blockIdx,
  active,
}: {
  sectionIdx: number;
  blockIdx: number;
  active: boolean;
}) {
  return (
    <div
      style={{
        minWidth: 36,
        height: 24,
        borderRadius: 12,
        background: active ? 'var(--mantine-color-dark-7)' : 'var(--mantine-color-gray-2)',
        color: active ? 'white' : 'var(--mantine-color-gray-7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontWeight: 500,
        fontSize: 12,
        padding: '0 8px',
        flexShrink: 0,
      }}
    >
      {sectionIdx + 1}.{blockIdx + 1}
    </div>
  );
}
